import { Injectable } from '@angular/core';
import { PrimusService } from './primus.service';

/**
 * FirmwareUpdateRequesterService
 *
 * Handles firmware update requests following the same pattern as screenshot requests.
 *
 * Flow:
 * 1. UI creates Primus (WebSocket) connection to Core
 * 2. Waits for 'client-connected' message with clientId from Core
 * 3. Calls backend API with displayId, clientId, and firmwareUrl
 * 4. Backend sends 'firmware-upgrade-request' message to Viewer via Core
 *    Message format: { msg: "firmware-upgrade-request", url: firmwareUrl, clientId: clientId }
 * 5. Viewer receives message and calls player.requestFirmwareUpgrade(url, callback)
 * 6. Player invokes Luna service: luna://.../scapservice/firmwareUpgrade with {url, subscribe: true}
 * 7. Viewer sends status updates back: 'firmware-upgrade-started', 'firmware-upgrade-complete', 'firmware-upgrade-failed'
 * 8. Core relays messages back through Primus connection
 *
 * Note: Firmware upgrade can take up to 20 minutes. Player will reboot automatically after successful upgrade.
 */
@Injectable({
  providedIn: 'root',
})
export class FirmwareUpdateRequesterService {
  constructor(private primusService: PrimusService) {}

  request(coreRequester: Function): Promise<any> {
    return new Promise((resolve, reject) => {
      this.primusService.create().then((primus) => {
        // Firmware updates can take up to 20 minutes
        const TWENTY_MINUTES = 1200000;
        const timer = setTimeout(() => {
          primus.end();
          reject('timeout');
        }, TWENTY_MINUTES);

        primus.on('data', (data) => {
          if (data.msg === 'client-connected') {
            // Core has connected, now send firmware upgrade request with the clientId
            coreRequester(data.clientId).then(null, (err) => {
              clearTimeout(timer);
              primus.end();
              reject(err);
            });
          } else if (data.msg === 'firmware-upgrade-started') {
            // Player has started the firmware upgrade process
            // Don't clear timer or end connection yet - wait for completion
            console.log('Firmware upgrade started', data);
          } else if (data.msg === 'firmware-upgrade-complete') {
            // Player has successfully completed the firmware upgrade
            clearTimeout(timer);
            primus.end();
            resolve(data);
          } else if (data.msg === 'firmware-upgrade-failed') {
            // Player firmware upgrade failed
            clearTimeout(timer);
            primus.end();
            reject('firmware-upgrade-failed');
          }
        });

        primus.on('error', (err) => {
          primus.end();
          clearTimeout(timer);
          reject(err);
        });
      });
    });
  }
}
